/**
 * rbacConfig.js
 * Pure-JS RBAC helpers.  All data is read from localStorage so these
 * functions work outside React (e.g. in route guards, util modules, etc.).
 *
 * Role model: resource-based strings such as SESSION_ADMIN, LOCATION_ADMIN,
 * PRODUCT_ADMIN, ORGANISATION_ADMIN — stored in localStorage.selectedLocationRoles
 * as a JSON array by LocationModal after getUserRolesById resolves.
 */

import rbacConfig from "../config/rbac.json";

/* ── Internal helpers ─────────────────────────────────── */

/**
 * Read the current user's location-specific roles from localStorage.
 *
 * ONLY `selectedLocationRoles` is consulted for screen/action/widget gating.
 * This key is:
 *   - Written by `getUserRolesById` in LocationModal after each location selection
 *   - Cleared immediately on location switch (before the async fetch returns)
 *   - Location-scoped: it represents the user's roles *at the current branch*
 *
 * Why NOT union with `user_roles`, `userRoles`, or login-embedded roles:
 *   Those keys carry org-level or session-level data that is NOT cleared on
 *   location switch. Unioning them would let a user who was LOCATION_ADMIN at
 *   branch A retain admin access at branch B until the page reloads, defeating
 *   the location-scoped RBAC guarantee. While `selectedLocationRoles` is being
 *   fetched (i.e. it is empty/cleared), the decision correctly defaults to deny,
 *   preventing temporary privilege escalation during the transition window.
 *
 * Returns a Set<string> of uppercase role strings (empty set = no roles / loading).
 */
function getCurrentRoles() {
  try {
    const raw = localStorage.getItem("selectedLocationRoles");
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((r) => String(r).toUpperCase()));
  } catch {
    return new Set();
  }
}

/**
 * Read the current user's permissions from localStorage.
 * Returns a plain object (permissionKey → boolean|object).
 */
function getCurrentPermissions() {
  try {
    const raw = localStorage.getItem("userPermissions");
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

/* ── Public API ───────────────────────────────────────── */

/**
 * canViewScreen(screenKey)
 * Returns true if the current user may view the given screen.
 *
 * If the screen is not listed in rbac.json, access is granted (open by default).
 * If requiredRoles is empty, access is granted.
 * Otherwise, the user must have at least ONE of the required roles.
 *
 * @param {string} screenKey - key matching rbac.json "screens" (e.g. "account-subscription")
 * @returns {boolean}
 */
export function canViewScreen(screenKey) {
  const screen = rbacConfig.screens?.[screenKey];
  if (!screen) return true; // not restricted — open by default
  const required = screen.requiredRoles;
  if (!required || required.length === 0) return true;
  const userRoles = getCurrentRoles();
  return required.some((r) => userRoles.has(r.toUpperCase()));
}

/**
 * canDoAction(actionKey)
 * Returns true if the current user may perform the given action.
 * Actions not listed in rbac.json default to permitted.
 *
 * @param {string} actionKey
 * @returns {boolean}
 */
export function canDoAction(actionKey) {
  const action = rbacConfig.actions?.[actionKey];
  if (!action) return true;
  const required = action.requiredRoles;
  if (!required || required.length === 0) return true;
  const userRoles = getCurrentRoles();
  return required.some((r) => userRoles.has(r.toUpperCase()));
}

/**
 * canSeeWidget(widgetKey)
 * Returns true if the current user may see the given widget/UI element.
 * Widgets not listed in rbac.json default to visible.
 *
 * @param {string} widgetKey
 * @returns {boolean}
 */
export function canSeeWidget(widgetKey) {
  const widget = rbacConfig.widgets?.[widgetKey];
  if (!widget) return true;
  const required = widget.requiredRoles;
  if (!required || required.length === 0) return true;
  const userRoles = getCurrentRoles();
  return required.some((r) => userRoles.has(r.toUpperCase()));
}

/**
 * refreshRbacConfig()
 * Called once on App mount to ensure the in-memory role set reflects the
 * latest localStorage state (e.g. after a page reload between location
 * switches where roles may have been written after the module first loaded).
 *
 * Because getCurrentRoles() reads localStorage fresh on every call, this
 * function currently serves as a no-op hook for future caching if needed,
 * and as a clear mount-time signal that RBAC is initialised.
 */
export function refreshRbacConfig() {
  // Reads are already live from localStorage on every canView*/canDo*/canSee* call.
  // This function exists as an explicit initialisation hook so App.js can
  // document the intent and allow future caching logic here.
  return true;
}

/**
 * hasPosManagerRole()
 * Returns true if the current user holds the POS_MANAGER role at the
 * selected location.  Uses the same localStorage key as all other RBAC
 * helpers so it stays in sync with location switches.
 *
 * @returns {boolean}
 */
export function hasPosManagerRole() {
  return getCurrentRoles().has("POS_MANAGER");
}
